/**
 * RoleOnboardingTour - 3-step tour tailored to the user's role.
 *
 * Shows on first login after the disclaimer is accepted. Each role
 * sees a different set of steps focused on THEIR daily workflow.
 */
import { useState } from "react";

import {
  ArrowRight, CheckCircle2, X,
  Settings, Users, Upload, FolderOpen, FileText,
  ClipboardList, Clock, Calendar, DollarSign, Layers, BarChart3,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import { useGetDisclaimerStatusQuery } from "@/features/auth/api/authApi";
import { useAuth } from "@/features/auth/model/useAuth";

import { useVerticalVocab } from "@/shared/lib/verticals";
import type { VerticalVocab } from "@/shared/lib/verticals";
import { Button } from "@/shared/ui/Button";

interface TourStep {
  title: string;
  description: string;
  route: string;
  icon: typeof Settings;
}

function getTourSteps(role: string, v: VerticalVocab): TourStep[] {
  switch (role) {
    case "OWNER":
    case "ADMIN":
      return [
        { title: "Set up your organization", description: "Check your settings, review your org type, and make sure everything looks right.", route: "/settings", icon: Settings },
        { title: "Invite your team", description: "Add your staff, volunteers, and board members so they can start using the platform.", route: "/team/invitations", icon: Users },
        { title: "Import your data", description: "Upload your existing spreadsheet to bring in your people, donors, and volunteers.", route: "/data", icon: Upload },
      ];
    case "STAFF":
      return [
        { title: `Your ${v.cases.plural}`, description: `This is where you'll spend most of your time - managing ${v.cases.plural}, logging services, and tracking progress.`, route: "/cases", icon: FolderOpen },
        { title: "Log an interaction", description: "Open any person's profile and log a call, visit, meeting, or note. This builds their timeline.", route: "/community", icon: FileText },
        { title: "Check your dashboard", description: "Your dashboard shows the metrics that matter most. Start each day here.", route: "/", icon: BarChart3 },
      ];
    case "VOLUNTEER":
      return [
        { title: "Check people in", description: "The check-in page is your main tool - search a name, tap a purpose, and you're done.", route: "/check-in", icon: ClipboardList },
        { title: "Log your hours", description: "After your shift, record your hours so your coordinator can track volunteer engagement.", route: "/volunteer-hours", icon: Clock },
        { title: "View your schedule", description: "See upcoming shifts and sign up for open ones.", route: "/scheduling", icon: Calendar },
      ];
    case "MENTOR":
      return [
        { title: `Your ${v.cases.plural}`, description: `These are the people you're matched with. Click "My ${v.cases.plural}" to see just yours.`, route: "/cases", icon: FolderOpen },
        { title: "Log a session", description: "After meeting with your mentee, open their case and log the session details.", route: "/cases", icon: FileText },
        { title: "Add a note", description: "Case notes capture what happened - goals discussed, progress made, concerns.", route: "/cases", icon: FileText },
      ];
    case "BOARD":
    case "BOARD_MEMBER":
      return [
        { title: "Your dashboard", description: "The dashboard gives you a high-level view of how the organization is performing.", route: "/", icon: BarChart3 },
        { title: "Board report", description: "A printable 90-day summary with trends - perfect for board meetings.", route: "/board-report", icon: Layers },
        { title: "That's it!", description: "You'll get a login link before each board meeting. No password needed.", route: "/", icon: CheckCircle2 },
      ];
    case "ACCOUNTANT":
      return [
        { title: "Funds & accounts", description: "Review your fund structure and account balances.", route: "/accounting", icon: DollarSign },
        { title: "Record contributions", description: "Log individual gifts or use batch mode for Sunday offerings.", route: "/accounting", icon: DollarSign },
        { title: "Run a report", description: "Check Financial Reporting for date-range metrics you can print for leadership.", route: "/accounting?section=reporting", icon: BarChart3 },
      ];
    default:
      return [
        { title: "Explore the dashboard", description: "Your dashboard shows the key metrics for your organization.", route: "/", icon: BarChart3 },
        { title: "Browse your people", description: "Community and Team directories split donors, recipients, and your operational team.", route: "/community", icon: Users },
        { title: "Try the copilot", description: "The chat bubble in the bottom-right is your AI assistant. Ask it anything!", route: "/", icon: FileText },
      ];
  }
}

export function RoleOnboardingTour() {
  const { user } = useAuth();
  const v = useVerticalVocab();
  const navigate = useNavigate();
  const { data: disclaimerStatus } = useGetDisclaimerStatusQuery();
  const role = user?.role || "STAFF";
  const storageKey = `tnn.role-tour.${role}`;

  const [currentStep, setCurrentStep] = useState(0);
  const [dismissed, setDismissed] = useState(() =>
    localStorage.getItem(storageKey) === "done"
  );

  const steps = getTourSteps(role, v);

  const dismiss = () => {
    setDismissed(true);
    localStorage.setItem(storageKey, "done");
  };

  const advance = () => {
    const current = steps[currentStep];
    if (!current) return;
    if (currentStep < steps.length - 1) {
      navigate(current.route);
      setCurrentStep((s) => s + 1);
    } else {
      navigate(current.route);
      dismiss();
    }
  };

  if (dismissed || !user || steps.length === 0) return null;
  if (!disclaimerStatus?.accepted) return null;

  const step = steps[currentStep];
  if (!step) return null;
  const StepIcon = step.icon;

  return (
    <div className="tnn-overlay fixed inset-0 z-[55] flex items-center justify-center bg-black/40">
      <div className="bg-card rounded-xl shadow-lg border border-border max-w-md w-full mx-4 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex gap-1.5">
            {steps.map((_, i) => (
              <div key={i} className={`h-1.5 w-8 rounded-full ${i <= currentStep ? "bg-primary" : "bg-muted"}`} />
            ))}
          </div>
          <button type="button" onClick={dismiss} className="text-muted-foreground hover:text-foreground p-1">
            <X className="size-4" />
          </button>
        </div>

        <div className="flex items-start gap-3 mb-6">
          <div className="rounded-lg bg-primary/10 p-2.5">
            <StepIcon className="size-5 text-primary" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-foreground mb-1">{step.title}</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">{step.description}</p>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <button type="button" onClick={dismiss} className="text-xs text-muted-foreground hover:text-foreground" data-testid="role-tour-skip">
            Skip tour
          </button>
          <Button variant="primary" onClick={advance}>
            {currentStep === steps.length - 1 ? "Get started" : "Next"}
            <ArrowRight className="size-3.5 ml-1.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
